import React from 'react';
import {
  Box,
  Card,
  CardBody,
  Text,
  VStack,
  HStack,
  Icon,
  Badge,
  Button,
  SimpleGrid,
  useColorModeValue,
} from '@chakra-ui/react';
import { FiFileText, FiUpload, FiDownload, FiEye } from 'react-icons/fi';

const VendorDocuments: React.FC = () => {
  const bgColor = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.600');

  const documentCategories = [
    {
      title: 'Contracts & Agreements',
      count: 3,
      color: 'blue',
      documents: ['Master Service Agreement', 'Privacy Agreement', 'NDA'],
    },
    {
      title: 'Certifications',
      count: 5,
      color: 'green',
      documents: ['ISO 9001', 'Safety Certification', 'Quality Standards'],
    },
    {
      title: 'Proposals & Submissions',
      count: 8,
      color: 'purple',
      documents: ['Q1 2024 Proposal', 'Custom Furniture Bid', 'Installation Quote'],
    },
    {
      title: 'Compliance Documents',
      count: 4,
      color: 'orange',
      documents: ['Insurance Certificate', 'Business License', 'Tax Documentation'],
    },
  ];

  return (
    <Box maxW="1400px" mx="auto" p={6}>
      <VStack spacing={6} align="stretch">
        {/* Header */}
        <Card bg={bgColor} borderColor={borderColor}>
          <CardBody>
            <HStack justify="space-between">
              <VStack align="start" spacing={1}>
                <Text fontSize="2xl" fontWeight="bold">
                  Document Library
                </Text>
                <Text color="gray.600">
                  Manage contracts, specifications, proposals, and compliance documents
                </Text>
              </VStack>
              <Button leftIcon={<Icon as={FiUpload} />} colorScheme="blue">
                Upload Document
              </Button>
            </HStack>
          </CardBody>
        </Card>

        {/* Document Categories */}
        <SimpleGrid columns={{ base: 1, md: 2, lg: 4 }} spacing={6}>
          {documentCategories.map((category) => (
            <Card key={category.title} bg={bgColor} borderColor={borderColor}>
              <CardBody>
                <VStack spacing={4}>
                  <HStack w="full" justify="space-between">
                    <Icon as={FiFileText} color={`${category.color}.500`} boxSize={6} />
                    <Badge colorScheme={category.color}>{category.count}</Badge>
                  </HStack>
                  
                  <VStack spacing={2}>
                    <Text fontWeight="semibold" textAlign="center">
                      {category.title}
                    </Text>
                    <VStack spacing={1}>
                      {category.documents.slice(0, 3).map((doc, index) => (
                        <Text key={index} fontSize="xs" color="gray.600" textAlign="center">
                          {doc}
                        </Text>
                      ))}
                    </VStack>
                  </VStack>

                  <Button size="sm" variant="outline" colorScheme={category.color} width="full">
                    View All
                  </Button>
                </VStack>
              </CardBody>
            </Card>
          ))}
        </SimpleGrid>

        {/* Recent Documents */}
        <Card bg={bgColor} borderColor={borderColor}>
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontSize="lg" fontWeight="semibold">
                Recent Documents
              </Text>
              
              <VStack spacing={3} align="stretch">
                {[
                  { name: 'Master Service Agreement - 2024', type: 'Contract', status: 'Signed', date: '2024-01-15' },
                  { name: 'Q1 Furniture Proposal', type: 'Proposal', status: 'Under Review', date: '2024-01-10' },
                  { name: 'ISO 9001 Certificate', type: 'Certification', status: 'Valid', date: '2024-01-05' },
                  { name: 'Installation Guidelines v2.1', type: 'Specification', status: 'Approved', date: '2024-01-03' },
                ].map((doc, index) => (
                  <HStack key={index} justify="space-between" p={3} bg="gray.50" borderRadius="md">
                    <HStack spacing={3}>
                      <Icon as={FiFileText} color="blue.500" />
                      <VStack align="start" spacing={0}>
                        <Text fontSize="sm" fontWeight="medium">{doc.name}</Text>
                        <Text fontSize="xs" color="gray.600">{doc.type} • {doc.date}</Text>
                      </VStack>
                    </HStack>
                    
                    <HStack spacing={2}>
                      <Badge 
                        colorScheme={
                          doc.status === 'Signed' || doc.status === 'Approved' || doc.status === 'Valid' 
                            ? 'green' 
                            : 'orange'
                        }
                        size="sm"
                      >
                        {doc.status}
                      </Badge>
                      <Button size="xs" variant="ghost">
                        <Icon as={FiEye} />
                      </Button>
                      <Button size="xs" variant="ghost">
                        <Icon as={FiDownload} />
                      </Button>
                    </HStack>
                  </HStack>
                ))}
              </VStack>
            </VStack>
          </CardBody>
        </Card>

        {/* Development Notice */}
        <Card bg="blue.50" borderColor="blue.300">
          <CardBody>
            <VStack spacing={2}>
              <Text fontSize="lg" fontWeight="semibold" color="blue.700">
                Document Management Coming Soon
              </Text>
              <Text color="blue.600" textAlign="center">
                Full document management features including upload, versioning, digital signatures, 
                and collaborative review workflows are in development.
              </Text>
            </VStack>
          </CardBody>
        </Card>
      </VStack>
    </Box>
  );
};

export default VendorDocuments; 